import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SelectSearchInput from '../../SelectSearch';
import { toast } from 'react-toastify';
import MenuCostTable from './MenuCostTable';
import MenuCostForm from './MenuCostForm';

const MenuCostPrice = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [menuItems, setMenuItems] = useState([]);
    const [unitDefinitions, setUnitDefinitions] = useState([]);
    const [subRecipes, setSubRecipes] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [costItems, setCostItems] = useState([]);
    const [subRecipeItems, setSubRecipeItems] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingSubRecipeIndex, setEditingSubRecipeIndex] = useState(null);
    const [showSubRecipe, setShowSubRecipe] = useState(false);
    const [type, setType] = useState('');
    const [category, setCategory] = useState('');
    const [autoFill, setAutoFill] = useState(false);
    const [manualTotalCost, setManualTotalCost] = useState(0);

    const [formData, setFormData] = useState({
        menuItem: '',
        sellingPrice: '',
        costItem: '',
        rate: '',
        unit: '',
        uom: '',
        quantity: '',
        new_uom: ''
    });

    const [subRecipeFormData, setSubRecipeFormData] = useState({
        subRecipe: '',
        subRecipeName: '',
        quantity: '',
        uom: '',
        rate: '',
        unit: '',
        new_uom: ''
    });

    const kgVariations = useMemo(() => ['kg', 'kgs', 'kilogram', 'kilograms'], []);
    const gramVariations = useMemo(() => ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'], []);
    const literVariations = useMemo(() => ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'], []);
    const milliliterVariations = useMemo(() => ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls'], []);

    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.post(`${baseUrl}/item-lists`, {
                    token: token,
                    outlet: selectedOutlet
                });
                setMenuItems(response.data);
            } catch (error) {
                console.error('Error fetching menu items:', error);
            }
        };

        const fetchUnitDefinitions = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.get(`${baseUrl}/unit-definitions`, {
                    params: {
                        outlet: selectedOutlet,
                        token: token
                    }
                });
                setUnitDefinitions(response.data);
            } catch (error) {
                console.error('Error fetching unit definitions:', error);
            }
        };

        const fetchSubRecipes = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.get(`${baseUrl}/sub-recipes`, {
                    token: token
                }, {
                    params: {
                        outlet: selectedOutlet
                    }
                });
                setSubRecipes(response.data);
            } catch (error) {
                console.error('Error fetching sub-recipes:', error);
            }
        };

        fetchMenuItems();
        fetchUnitDefinitions();
        fetchSubRecipes();
    }, [token, selectedOutlet, baseUrl]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if ((name === 'unit' || name === 'quantity')) {
            if (value && isNaN(value)) return;
        }
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubRecipeInputChange = (e) => {
        const { name, value } = e.target;
        setSubRecipeFormData({
            ...subRecipeFormData,
            [name]: value
        });
    };

    const handleMenuItemSelect = (e) => {
        const itemName = e.target.value;
        const selectedItem = menuItems
            .flatMap(type => type.items.flatMap(category => category.items))
            .find(item => item.Name === itemName);

        if (selectedItem) {
            setFormData({
                ...formData,
                menuItem: selectedItem.Name,
                sellingPrice: selectedItem.Price,
                costItem: '',
                rate: '',
                unit: '',
                uom: '',
                quantity: '',
                new_uom: ''
            });

            setCategory(selectedItem.Description);
            setType(selectedItem.Type);
        }
    };

    const handleClearMenuItem = () => {
        setFormData({
            menuItem: '',
            sellingPrice: '',
            costItem: '',
            rate: '',
            unit: '',
            uom: '',
            quantity: '',
            new_uom: ''
        });
        setCostItems([]);
        setSubRecipeItems([]);
        setEditingIndex(null);
        setEditingSubRecipeIndex(null);
        setAutoFill(false);
    };

    const handleCostItemSelect = (e) => {
        const itemName = e.target.value;
        const selectedItem = unitDefinitions.find(item => item.name === itemName);

        if (selectedItem) {
            const showConversionOptions =
                kgVariations.includes(selectedItem.uom.toLowerCase()) ||
                literVariations.includes(selectedItem.uom.toLowerCase()) ||
                gramVariations.includes(selectedItem.uom.toLowerCase()) ||
                milliliterVariations.includes(selectedItem.uom.toLowerCase());

            let normalizedUom = selectedItem.uom.toLowerCase();

            if (kgVariations.includes(normalizedUom)) {
                normalizedUom = 'kg';
            } else if (gramVariations.includes(normalizedUom)) {
                normalizedUom = 'g';
            } else if (literVariations.includes(normalizedUom)) {
                normalizedUom = 'ltr';
            } else if (milliliterVariations.includes(normalizedUom)) {
                normalizedUom = 'ml';
            }

            setFormData({
                ...formData,
                costItem: selectedItem.name,
                rate: selectedItem.rate,
                unit: selectedItem.unit,
                uom: normalizedUom,
                new_uom: showConversionOptions ? '' : normalizedUom
            });
        }
    };

    const handleSubRecipeSelect = (e) => {
        const subRecipeId = parseInt(e.target.value);
        const selectedSubRecipe = subRecipes.find(recipe => recipe.id === subRecipeId);

        if (selectedSubRecipe) {
            const showConversionOptions =
                kgVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                gramVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                literVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                milliliterVariations.includes(selectedSubRecipe.uom.toLowerCase());

            let normalizedUom = selectedSubRecipe.uom.toLowerCase();

            if (kgVariations.includes(normalizedUom)) {
                normalizedUom = 'kg';
            } else if (gramVariations.includes(normalizedUom)) {
                normalizedUom = 'g';
            } else if (literVariations.includes(normalizedUom)) {
                normalizedUom = 'ltr';
            } else if (milliliterVariations.includes(normalizedUom)) {
                normalizedUom = 'ml';
            }

            setSubRecipeFormData({
                ...subRecipeFormData,
                subRecipe: selectedSubRecipe.id,
                subRecipeName: selectedSubRecipe.name,
                rate: selectedSubRecipe.costprice || '',
                uom: selectedSubRecipe.uom || '',
                unit: selectedSubRecipe.unit || '',
                new_uom: showConversionOptions ? '' : normalizedUom
            });
        }
    };

    const handleUomChange = (e) => {
        const new_uom = e.target.value;
        setFormData({
            ...formData,
            new_uom: new_uom
        });
    };

    const handleAddCostItem = (e) => {
        e.preventDefault();

        if (!formData.menuItem || !formData.costItem || !formData.quantity ||
            (['kg', 'ltr'].includes(formData.uom) && !formData.new_uom)) {
            toast.info('Please fill all required fields');
            return;
        }

        const exists = costItems.some(
            (item, idx) => item.costItem === formData.costItem &&
                item.menuItem === formData.menuItem &&
                idx !== editingIndex
        );
        if (exists) {
            toast.info('This ingredient already exists');
            return;
        }

        let calculatedUnit = parseFloat(formData.unit);
        let calculatedQuantity = parseFloat(formData.quantity);
        let costPrice;

        if (formData.new_uom === 'gm' && formData.uom === 'kg') {
            calculatedQuantity /= 1000;
        } else if (formData.new_uom === 'kg' && formData.uom === 'gm') {
            calculatedQuantity *= 1000;
        } else if (formData.new_uom === 'ml' && formData.uom === 'ltr') {
            calculatedQuantity /= 1000;
        } else if (formData.new_uom === 'ltr' && formData.uom === 'ml') {
            calculatedQuantity *= 1000;
        }

        costPrice = (calculatedQuantity / calculatedUnit) * parseFloat(formData.rate);

        const newCostItem = {
            menuItem: formData.menuItem,
            sellingPrice: formData.sellingPrice,
            costItem: formData.costItem,
            rate: formData.rate,
            unit: formData.unit,
            uom: formData.uom,
            new_uom: formData.new_uom || formData.uom,
            quantity: formData.quantity,
            costPrice: costPrice.toFixed(2)
        };

        if (editingIndex !== null) {
            const updatedItems = [...costItems];
            updatedItems[editingIndex] = newCostItem;
            setCostItems(updatedItems);
            setEditingIndex(null);
        } else {
            setCostItems([...costItems, newCostItem]);
        }

        setFormData({
            ...formData,
            costItem: '',
            rate: '',
            unit: '',
            uom: '',
            quantity: '',
            new_uom: ''
        });
    };

    const handleAddSubRecipe = (e) => {
        e.preventDefault();

        if (!subRecipeFormData.subRecipe || !subRecipeFormData.quantity ||
            !subRecipeFormData.subRecipeName ||
            (['kg', 'ltr'].includes(subRecipeFormData.uom) && !subRecipeFormData.new_uom)) {
            toast.info('Please fill all required fields');
            return;
        }

        const exists = subRecipeItems.some(
            item => item.subRecipeId === subRecipeFormData.subRecipe
        );
        if (exists && editingSubRecipeIndex === null) {
            toast.info('This sub-recipe already exists');
            return;
        }

        let calculatedUnit = parseFloat(subRecipeFormData.unit);
        let calculatedQuantity = parseFloat(subRecipeFormData.quantity);
        let costPrice;

        if (subRecipeFormData.new_uom === 'gm' && subRecipeFormData.uom === 'kg') {
            calculatedUnit *= 1000;
        } else if (subRecipeFormData.new_uom === 'kg' && subRecipeFormData.uom === 'gm') {
            calculatedUnit /= 1000;
        } else if (subRecipeFormData.new_uom === 'ml' && subRecipeFormData.uom === 'ltr') {
            calculatedUnit *= 1000;
        } else if (subRecipeFormData.new_uom === 'ltr' && subRecipeFormData.uom === 'ml') {
            calculatedUnit /= 1000;
        }
        costPrice = (calculatedQuantity / calculatedUnit) * parseFloat(subRecipeFormData.rate);

        const newSubRecipeItem = {
            subRecipeId: subRecipeFormData.subRecipe,
            quantity: calculatedQuantity,
            uom: subRecipeFormData.uom,
            unit: subRecipeFormData.unit,
            rate: subRecipeFormData.rate,
            costprice: costPrice.toFixed(2),
            subRecipeName: subRecipeFormData.subRecipeName,
            new_uom: subRecipeFormData.new_uom || subRecipeFormData.uom
        };

        if (editingSubRecipeIndex !== null) {
            const updatedItems = [...subRecipeItems];
            updatedItems[editingSubRecipeIndex] = newSubRecipeItem;
            setSubRecipeItems(updatedItems);
            setEditingSubRecipeIndex(null);
        } else {
            setSubRecipeItems([...subRecipeItems, newSubRecipeItem]);
        }

        setSubRecipeFormData({
            subRecipe: '',
            subRecipeName: '',
            quantity: '',
            uom: '',
            rate: '',
            costPrice: '',
            unit: ''
        });
    };

    const handleEdit = (index) => {
        if (index < 0 || index >= costItems.length) return;

        setEditingIndex(index);
        const item = costItems[index];
        setFormData(prev => ({
            ...prev,
            costItem: item.costItem,
            rate: item.rate,
            unit: item.unit,
            uom: item.uom,
            originalUom: item.originalUom,
            quantity: item.quantity.toString(),
            new_uom: item.new_uom
        }));
    };

    const handleSubRecipeEdit = (index) => {
        if (index < 0 || index >= subRecipeItems.length) return;

        setEditingSubRecipeIndex(index);
        const item = subRecipeItems[index];
        setSubRecipeFormData(prev => ({
            ...subRecipeFormData,
            subRecipe: item.subRecipeId,
            subRecipeName: item.subRecipeName,
            quantity: item.quantity.toString(),
            uom: item.uom,
            unit: item.unit,
            rate: item.rate,
            new_uom: item.new_uom
        }));
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setFormData({
            ...formData,
            costItem: '',
            rate: '',
            unit: '',
            uom: '',
            quantity: '',
            new_uom: ''
        });
    };

    const handleCancelSubRecipeEdit = () => {
        setEditingSubRecipeIndex(null);
        setSubRecipeFormData({
            subRecipe: '',
            subRecipeName: '',
            quantity: '',
            uom: '',
            rate: '',
            unit: '',
            new_uom: ''
        });
    };

    const handleDelete = (index) => {
        const updatedItems = costItems.filter((_, i) => i !== index);
        setCostItems(updatedItems);
        if (editingIndex === index) {
            handleCancelEdit();
        }
    };

    const handleSubRecipeDelete = (index) => {
        const updatedItems = subRecipeItems.filter((_, i) => i !== index);
        setSubRecipeItems(updatedItems);
        if (editingSubRecipeIndex === index) {
            handleCancelSubRecipeEdit();
        }
    };

    const convertQuantityToBaseUnit = (
        quantity,
        uom,
        new_uom,
        kgVariations,
        gramVariations,
        literVariations,
        milliliterVariations
    ) => {
        let converted = parseFloat(quantity);

        if (new_uom && uom !== new_uom) {
            if ((kgVariations.includes(uom.toLowerCase())) &&
                (gramVariations.includes(new_uom.toLowerCase()))) {
                converted = converted / 1000;
            }
            else if ((gramVariations.includes(uom.toLowerCase())) &&
                (kgVariations.includes(new_uom.toLowerCase()))) {
                converted = converted * 1000;
            }
            else if ((literVariations.includes(uom.toLowerCase())) &&
                (milliliterVariations.includes(new_uom.toLowerCase()))) {
                converted = converted / 1000;
            }
            else if ((milliliterVariations.includes(uom.toLowerCase())) &&
                (literVariations.includes(new_uom.toLowerCase()))) {
                converted = converted * 1000;
            }
        }

        return converted;
    };

    const showSaveConfirmation = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    name: formData.menuItem,
                                    cost_price: manualTotalCost,
                                    selling_price: formData.sellingPrice,
                                    cost_percent: profitPercentage,
                                    items: costItems.map(item => ({
                                        name: item.costItem,
                                        rate: item.rate,
                                        uom: item.uom,
                                        new_uom: item.new_uom || item.uom,
                                        quantity: convertQuantityToBaseUnit(
                                            item.quantity,
                                            item.uom,
                                            item.new_uom,
                                            kgVariations,
                                            gramVariations,
                                            literVariations,
                                            milliliterVariations
                                        ).toString(),
                                        cost: item.costPrice,
                                        unit: item.unit
                                    })),
                                    sub_recipes: subRecipeItems.map(item => ({
                                        sub_recipe_id: item.subRecipeId,
                                        quantity: convertQuantityToBaseUnit(
                                            item.quantity,
                                            item.uom,
                                            item.new_uom,
                                            kgVariations,
                                            gramVariations,
                                            literVariations,
                                            milliliterVariations
                                        ).toString(),
                                        uom: item.uom,
                                        new_uom: item.new_uom || item.uom,
                                        rate: item.rate,
                                        costprice: item.costprice,
                                        unit: item.unit
                                    })),
                                    item_type: type,
                                    category: category
                                };

                                await axios.post(`${baseUrl}/recipe`, payload);
                                toast.success("Recipe saved successfully!");

                                setCostItems([]);
                                setSubRecipeItems([]);
                                setManualTotalCost(0);
                                setFormData({
                                    menuItem: '',
                                    sellingPrice: '',
                                    costItem: '',
                                    rate: '',
                                    unit: '',
                                    uom: '',
                                    quantity: '',
                                    new_uom: '',
                                });
                                setSubRecipeFormData({
                                    subRecipe: '',
                                    subRecipeName: '',
                                    quantity: '',
                                    uom: '',
                                    rate: '',
                                    unit: '',
                                    new_uom: ''
                                });
                            } catch (error) {
                                console.error('Error saving recipe:', error);
                                toast.error('Failed to save recipe');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-cost-confirmation'
            }
        );
    };

    const handleSave = async () => {
        showSaveConfirmation();
    };

    const allMenuItemNames = menuItems
        .flatMap(type => type.items.flatMap(category => category.items))
        .map(item => item.Name);

    const allCostItemNames = unitDefinitions.map(item => item.name);

    const costItemsTotal = costItems.reduce((sum, item) => sum + parseFloat(item.costPrice), 0);
    const subRecipesTotal = subRecipeItems.reduce((sum, item) => sum + parseFloat(item.costprice), 0);
    const totalCost = (costItemsTotal + subRecipesTotal).toFixed(2);

    useEffect(() => {
        setManualTotalCost(totalCost);
    }, [totalCost]);

    const profitPercentage = (parseFloat(formData.sellingPrice || 0) > 0
        ? ((parseFloat(manualTotalCost) / parseFloat(formData.sellingPrice)) * 100).toFixed(2)
        : 0);

    const showConversionOptions = useMemo(() => {
        return (
            kgVariations.includes(formData.uom?.toLowerCase()) ||
            gramVariations.includes(formData.uom?.toLowerCase()) ||
            literVariations.includes(formData.uom?.toLowerCase()) ||
            milliliterVariations.includes(formData.uom?.toLowerCase())
        );
    }, [formData.uom, kgVariations, gramVariations, literVariations, milliliterVariations]);

    const showSubRecipeConversionOptions = useMemo(() => {
        return (
            kgVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            gramVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            literVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            milliliterVariations.includes(subRecipeFormData.uom?.toLowerCase())
        );
    }, [subRecipeFormData.uom, kgVariations, gramVariations, literVariations, milliliterVariations]);

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto mt-5 text-sm">
                    <div className="flex justify-center text-center mb-5">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <h1 className="text-xl font-bold mb-4">Recipe Cost Calculation</h1>

                    <MenuCostForm
                        handleAddCostItem={handleAddCostItem}
                        formData={formData}
                        handleMenuItemSelect={handleMenuItemSelect}
                        allCostItemNames={allCostItemNames}
                        allMenuItemNames={allMenuItemNames}
                        handleClearMenuItem={handleClearMenuItem}
                        handleInputChange={handleInputChange}
                        handleSubRecipeInputChange={handleSubRecipeInputChange}
                        handleSubRecipeSelect={handleSubRecipeSelect}
                        handleAddSubRecipe={handleAddSubRecipe}
                        handleCancelEdit={handleCancelEdit}
                        handleCancelSubRecipeEdit={handleCancelSubRecipeEdit}
                        handleCostItemSelect={handleCostItemSelect}
                        showConversionOptions={showConversionOptions}
                        showSubRecipeConversionOptions={showSubRecipeConversionOptions}
                        showSubRecipe={showSubRecipe}
                        setShowSubRecipe={setShowSubRecipe}
                        handleUomChange={handleUomChange}
                        editingIndex={editingIndex}
                        kgVariations={kgVariations}
                        literVariations={literVariations}
                        gramVariations={gramVariations}
                        milliliterVariations={milliliterVariations}
                        subRecipes={subRecipes}
                        subRecipeFormData={subRecipeFormData}
                        editingSubRecipeIndex={editingSubRecipeIndex}
                        menuItems={menuItems}
                        unitDefinitions={unitDefinitions}
                        autoFill={autoFill}
                        setAutoFill={setAutoFill}
                    />

                    <MenuCostTable
                        costItems={costItems}
                        subRecipeItems={subRecipeItems}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleSave={handleSave}
                        formData={formData}
                        manualTotalCost={manualTotalCost}
                        setManualTotalCost={setManualTotalCost}
                        profitPercentage={profitPercentage}
                        handleSubRecipeDelete={handleSubRecipeDelete}
                        handleSubRecipeEdit={handleSubRecipeEdit}
                        setFormData={setFormData}
                    />

                </div>
            </div>

            <Footer />
        </div>
    );
};

export default MenuCostPrice;